import { Alert } from "@common/controls/Alert/component";
import Button from "@common/controls/Button/component";
import { FormFieldContainer } from "@common/controls/Form/Field/component";
import { FormButtons, FormControlled, FormField } from "@common/controls/Form/component";
import TextInput from "@common/controls/Inputs/Text/component";
import { FormModalBase } from "@common/controls/Modal/Modals/Form/component";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import date from "@common/lib/date";
import { i18nFormat } from "@common/lib/i18n";
import Network from "@common/modules/network/types";
import * as React from "react";

export type OfferLinksModalProps = {
  offer: Network.Offers.Api.UserOffer;
  onClose: () => any;
  urls: string[];
};

export const OfferLinksModal = (props: OfferLinksModalProps) => {
  const i18n = useI18n();

  const alert = React.useMemo(() => {
    if (!props.offer.startDate || !props.offer.endDate) return null;

    const currentDate = date.getCurrentLocalDate();
    const startDate = date.startOfDay(props.offer.startDate);
    const endDate = date.startOfDay(props.offer.endDate);

    if (currentDate < startDate)
      return (
        <Alert color={SystemColor.Gray}>
          {i18nFormat(i18n.infoOfferScheduled, ["DATE", date.formatDateSentence(props.offer.startDate)])}
        </Alert>
      );

    if (currentDate > endDate)
      return (
        <Alert color={SystemColor.Gray}>
          {i18nFormat(i18n.infoOfferExpired, ["DATE", date.formatDateSentence(props.offer.endDate)])}
        </Alert>
      );

    return (
      <Alert color={SystemColor.Orange}>
        {i18nFormat(i18n.infoOfferAvailable, ["DATE", date.formatDateSentence(props.offer.endDate)])}
      </Alert>
    );
  }, [i18n, props.offer.startDate, props.offer.endDate]);

  const isLinkDisabled = React.useMemo(() => {
    if (!props.offer.endDate) return false;

    const currentDate = date.getCurrentLocalDate();
    const endDate = date.startOfDay(props.offer.endDate);

    return currentDate > endDate;
  }, [props.offer.endDate]);

  const tooltip = React.useMemo(() => {
    if (!props.offer.startDate || !props.offer.endDate) return null;

    const currentDate = date.getCurrentLocalDate();
    const startDate = date.startOfDay(props.offer.startDate);
    const endDate = date.startOfDay(props.offer.endDate);

    if (currentDate < startDate)
      return i18nFormat(i18n.infoOfferScheduled, ["DATE", date.formatDateSentence(props.offer.startDate)]);

    if (currentDate > endDate)
      return i18nFormat(i18n.infoOfferExpired, ["DATE", date.formatDateSentence(props.offer.endDate)]);

    return null;
  }, [props.offer.startDate, props.offer.endDate]);

  return (
    <FormModalBase {...props} Form={FormControlled} title={props.offer.name} isVisible>
      {() => (
        <>
          {alert}
          <div dangerouslySetInnerHTML={{ __html: props.offer.description }} />
          <FormFieldContainer>
            <div className="fc fg xs">
              {props.urls.map((x) => (
                <FormField
                  key={x}
                  name={x}
                  value={x}
                  Component={TextInput}
                  componentProps={{ isReadonly: !isLinkDisabled }}
                  isDisabled={isLinkDisabled}
                  tooltip={tooltip}
                />
              ))}
            </div>
          </FormFieldContainer>

          <FormButtons>
            <Button type="white" onClick={props.onClose}>
              {i18n.close}
            </Button>
          </FormButtons>
        </>
      )}
    </FormModalBase>
  );
};
